import React, { useState, useEffect } from 'react';

import { Modal, Button, Switch, Input, Form, Space, Divider, notification, Row, Col } from 'antd';

import UserSettingsForm from '@components/users/user-settings';
import UserClients, { IUserConsignee } from '@components/users/user-clients';
import IBaseComponentProps from '@components/base-component-props';

import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';

import { exception } from '@extensions/notification';
import { getDateTimeLocal } from '@extensions/utils';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { IUser } from '@entities/user';
import { IConsignee } from '@entities/consignee';

import { UserType } from '@enums/user-type';
import { Permission, hasPermission } from '@enums/permission';
import { NotificationServiceType, enumLabel as notificationServiceTypeLabel } from '@enums/notification-service-type';

interface IEmployeeProps extends IBaseComponentProps {
    parentUserId: string | undefined;
    parentUserLogin: string | undefined;
    parentUserType: UserType | undefined;
    id?: string;
    onClose: (refreshRequired: boolean) => void;
}

const Employee = (props: IEmployeeProps) => {
    const { id, parentUserId, parentUserLogin, parentUserType, onClose, serverFetch } = props;

    const [userForm] = Form.useForm();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const [api, contextHolder] = notification.useNotification();

    const [entity, setEntity] = useState<IUser>();
    const [consignees, setConsignees] = useState<Array<IConsignee>>();
    const [userConsignees, setUserConsignees] = useState<Array<IUserConsignee>>();

    const [loading, setLoading] = useState<boolean>(false);
    const [allowSetPassword, setAllowSetPassword] = useState<boolean>(!id);
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageUsers));

    useEffect(() => {
        setLoading(true);

        if (id) {
            serverFetch(`users/${id}`, { method: 'GET' })
                .then((data: any) => {
                    data.login = data.login?.replace(`${parentUserLogin}/`, '');
                    setEntity(data);
                })
                .catch((ex: any) => {
                    exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                });
        } else {
            setEntity({
                id: undefined,
                parentUserId: parentUserId,
                login: undefined,
                email: undefined,
                fullName: undefined,
                isActive: undefined,
                phone: undefined,
                isArchived: false,
                type: UserType.Employee,
                userSettings: {
                    showOrders: true,
                    showBills: true,
                    showBalances: true,
                    showInventory: true,
                    showTransactions: true,
                    showDocuments: true,
                    disableAutomaticBillPayment: false,
                    consigneeCodes: [],
                },
                notificationServices: [],
            });
        }

        if (parentUserType == UserType.Supplier) {
            serverFetch('consignees', { method: 'GET', queryParams: { contractorIds: [parentUserId] } })
                .then((data: any) => {
                    setConsignees(data);
                })
                .catch((ex: any) => {
                    exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                });
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        if (entity && consignees) {
            const entities: Array<IUserConsignee> = [];
            consignees.map((c) => {
                const consignee = entity?.userSettings?.consigneeCodes?.find((e) => e === c.code);
                if (consignee) {
                    entities.push({ userId: parentUserId as string, code: c.code as number, isActive: true });
                } else {
                    entities.push({ userId: parentUserId as string, code: c.code as number, isActive: false });
                }
            });

            setUserConsignees(entities);
        }
    }, [entity, consignees]);

    const onFinish = () => {
        if (!entity) return;

        setLoading(true);

        entity.login = `${parentUserLogin}/${entity.login}`;

        serverFetch(`users`, { method: id ? 'PUT' : 'POST', bodyData: { user: entity } })
            .then(() => {
                setLoading(false);

                userForm.resetFields();

                onClose(true);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения пользователя', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderSettings = () => {
        if (!entity) return;

        return (
            <div style={{ marginTop: -15 }}>
                <UserSettingsForm
                    showCurrencyOperations={false}
                    userType={entity.type}
                    userSettings={entity.userSettings}
                    isViewOnly={isViewOnly}
                    onChange={(e) => setEntity({ ...entity, userSettings: e })}
                />

                {entity && entity.type != UserType.SubClient && entity.notificationServices && (
                    <>
                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                                Сервисы уведомлений
                            </Divider>
                        </Form.Item>
                        {entity.notificationServices.map((s) => {
                            return (
                                <Form.Item
                                    label={`${notificationServiceTypeLabel(s.type)} / ${s.source ?? '-'} / ${s.token ? s.token.substring(0, s.token.length > 15 ? 14 : s.token.length) : '-'}`}
                                    tooltip={
                                        s.type == NotificationServiceType.Telegram || s.type == NotificationServiceType.Max
                                            ? 'Управляется подпиской бота'
                                            : undefined
                                    }
                                    labelAlign='left'
                                    labelCol={{ span: s.cancelledOn ? 4 : 22 }}
                                    wrapperCol={{ span: s.cancelledOn ? 20 : 2 }}
                                    style={{ marginBottom: 0 }}
                                    valuePropName='checked'
                                    initialValue={s?.isActive}
                                    name={s.type}
                                >
                                    {s?.cancelledOn ? (
                                        <div style={{ color: 'var(--main-red)' }}>
                                            отменено пользователем {getDateTimeLocal(s.cancelledOn)}
                                        </div>
                                    ) : (
                                        <Switch
                                            size='small'
                                            style={{ float: 'right' }}
                                            disabled={
                                                isViewOnly ||
                                                s.type == NotificationServiceType.Telegram ||
                                                s.type == NotificationServiceType.Max
                                            }
                                            onChange={(value) => {
                                                let copyServices = [...(entity.notificationServices || [])];
                                                let currentService = copyServices.find((e) => e.type == s.type);
                                                if (currentService) currentService.isActive = value;

                                                setEntity({ ...entity, notificationServices: copyServices });
                                            }}
                                        />
                                    )}
                                </Form.Item>
                            );
                        })}
                    </>
                )}

                <UserClients
                    userConsignees={userConsignees || []}
                    isViewOnly={isViewOnly}
                    onChange={(e) =>
                        setEntity({
                            ...entity,
                            userSettings: entity.userSettings
                                ? { ...entity.userSettings, consigneeCodes: e.filter((c) => c.isActive).map((c) => c.code) }
                                : undefined,
                        })
                    }
                />
            </div>
        );
    };

    return (
        <Modal
            width={1024}
            title={`${id ? (isViewOnly ? 'Информация о сотруднике' : 'Изменить сотрудника') : 'Добавить сотрудника'}`}
            open={true}
            okText='Сохранить'
            onOk={() => userForm.submit()}
            onCancel={() => {
                userForm.resetFields();
                onClose(false);
            }}
        >
            {entity && (
                <Form form={userForm} colon={false} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} onFinish={onFinish}>
                    <Row>
                        <Col span={12}>
                            <Form.Item
                                initialValue={entity?.login}
                                required
                                validateFirst={true}
                                label='Логин'
                                name='login'
                                rules={[
                                    { required: true, message: 'Укажите логин' },
                                    {
                                        validator: async (_, value) => {
                                            const newLogin = encodeURIComponent(`${parentUserLogin}/${value}`);

                                            const isEmailExist = await serverFetch(`users/loginexist/${id}/${newLogin}`, { method: 'GET' })
                                                .then((data: any) => {
                                                    return data;
                                                })
                                                .catch((ex: any) => {
                                                    exception(api, 'Ошибка проверки логина', ex, () => d(userLoaded(undefined)));
                                                });

                                            if (!id && isEmailExist)
                                                return Promise.reject('Учетная запись с указанным логином уже существует');
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    prefix={`${parentUserLogin}/`}
                                    autoFocus
                                    disabled={isViewOnly || !!id}
                                    autoComplete='none'
                                    onChange={(data) => {
                                        setEntity({ ...entity, login: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item required label='Пароль' name='newPassword' rules={[{ required: !id, message: 'Укажите пароль' }]}>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input.Password
                                        placeholder={id && !allowSetPassword ? '*******' : ''}
                                        disabled={!allowSetPassword || isViewOnly}
                                        onChange={(data) => {
                                            setEntity({ ...entity, newPassword: data.target.value });
                                        }}
                                    />
                                    {id && (
                                        <Button
                                            disabled={isViewOnly}
                                            type='primary'
                                            onClick={() => {
                                                setAllowSetPassword(true);
                                            }}
                                        >
                                            Сменить пароль
                                        </Button>
                                    )}
                                </Space.Compact>
                            </Form.Item>
                            <Form.Item
                                initialValue={entity?.fullName}
                                required
                                label='ФИО'
                                name='fullName'
                                rules={[{ required: true, message: 'Укажите ФИО' }]}
                            >
                                <Input
                                    disabled={isViewOnly}
                                    onChange={(data) => {
                                        setEntity({ ...entity, fullName: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <PhoneInputFormItem
                                value={entity?.phone}
                                name='phone'
                                label='Телефон'
                                required={true}
                                disabled={isViewOnly}
                                onChange={(value) => {
                                    setEntity({ ...entity, phone: value });
                                }}
                            />
                            <Form.Item
                                initialValue={entity?.email}
                                validateFirst={true}
                                label='Email'
                                name='email'
                                rules={[
                                    { type: 'email', message: 'Неверный формат Email' },
                                    {
                                        validator: async (_, value) => {
                                            const isEmailExist = await serverFetch(`users/emailexist/${id}/${value}`, { method: 'GET' })
                                                .then((data: any) => {
                                                    return data;
                                                })
                                                .catch((ex: any) => {
                                                    exception(api, 'Ошибка проверки Email', ex, () => d(userLoaded(undefined)));
                                                });

                                            if (!id && isEmailExist)
                                                return Promise.reject('Учетная запись с указанным email уже существует');
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    disabled={isViewOnly}
                                    autoComplete='none'
                                    onChange={(data) => {
                                        setEntity({ ...entity, email: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label='Активно' name='isActive' valuePropName='checked'>
                                <Switch
                                    disabled={isViewOnly}
                                    defaultChecked={entity?.isActive}
                                    onChange={(value) => {
                                        setEntity({ ...entity, isActive: value });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col offset={1} span={11}>
                            {renderSettings()}
                        </Col>
                    </Row>
                </Form>
            )}

            {contextHolder}
        </Modal>
    );
};

export default Employee;
